/**
 * @file components/sidebar/ConversationActions.jsx
 * @description UI controls for renaming and deleting conversations
 * Now uses useConversations context and CSS modules for styling
 * 
 * @component ConversationActions
 */

import React, { useState, useEffect, useRef } from 'react';
import { MoreVertical, Edit2, Trash2, AlertTriangle } from 'lucide-react';

// --- Import specific context hook ---
import { useConversations } from '../../context/ConversationsContext';

import ConfirmationDialog from '../common/ConfirmationDialog';

// --- Import CSS module for styling ---
import styles from '../../styles/ConversationActions.module.css';

/**
 * Custom hook to handle clicks outside of a referenced element.
 */
const useOnClickOutside = (ref, handler) => {
    useEffect(() => {
        const listener = (event) => {
            if (!ref.current || ref.current.contains(event.target)) {
                return;
            }
            handler(event);
        };
        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);
        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler]);
};

/**
 * ConversationActions Component
 * Uses useConversations for delete operations
 * 
 * @param {object} props
 * @param {string} props.conversationId - The ID of the conversation
 * @param {string} props.currentTitle - The current title of the conversation
 * @param {function} props.onStartRename - Callback to start rename operation
 * @param {function} props.onActionComplete - Optional callback after successful action
 */
const ConversationActions = ({ conversationId, currentTitle, onStartRename, onActionComplete }) => {
    // --- Get delete function from ConversationsContext ---
    const { handleDeleteConversation } = useConversations();
    
    // UI State
    const [showMenu, setShowMenu] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const menuRef = useRef();
    
    useOnClickOutside(menuRef, () => setShowMenu(false));

    useEffect(() => {
        const handleKeyDown = (e) => (e.key === 'Escape' && setShowMenu(false));
        if (showMenu) document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showMenu]);

    const handleDeleteConfirm = async () => {
        setIsProcessing(true);
        setError(null);
        try {
            console.log(`ConversationActions: Deleting conversation ${conversationId}`);
            await handleDeleteConversation(conversationId);
            if (onActionComplete) onActionComplete('delete');
            setShowDeleteModal(false);
            console.log(`ConversationActions: Successfully deleted conversation ${conversationId}`);
        } catch (err) {
            console.error(`ConversationActions: Failed to delete conversation:`, err);
            setError(`Failed to delete: ${err.message}`);
        } finally {
            setIsProcessing(false);
        }
    };
    
    return (
        <div className={styles.container} ref={menuRef}>
            <button 
                className={styles.menuButton} 
                onClick={(e) => { 
                    e.stopPropagation(); 
                    setShowMenu(!showMenu); 
                }} 
                title="More options"
            >
                <MoreVertical size={18} />
            </button>
            
            {showMenu && (
                <div className={styles.dropdown}>
                    <button 
                        className={styles.menuItem} 
                        onClick={(e) => { 
                            e.stopPropagation(); 
                            setShowMenu(false); 
                            onStartRename(); 
                        }}
                    >
                        <Edit2 size={16} /> Rename
                    </button>
                    <button 
                        className={`${styles.menuItem} ${styles.menuItemDelete}`} 
                        onClick={(e) => { 
                            e.stopPropagation(); 
                            setShowMenu(false); 
                            setShowDeleteModal(true); 
                        }}
                    >
                        <Trash2 size={16} /> Delete
                    </button>
                </div>
            )}
            
            <ConfirmationDialog
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                title="Delete Conversation"
                message={`Are you sure you want to delete "<strong>${currentTitle}</strong>"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                isProcessing={isProcessing}
                error={error}
                variant="danger"
            />
        </div>
    );
};

export default React.memo(ConversationActions);